import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sun, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brands for sunglasses/classic collection
const FEATURED_BRANDS = [
  { name: "Ray-Ban", logo: null },
  { name: "Gucci", logo: null },
  { name: "Prada", logo: null },
  { name: "Dior", logo: null },
  { name: "Saint Laurent", logo: null },
  { name: "Tom Ford", logo: null },
  { name: "Chloé", logo: null },
  { name: "Fendi", logo: null },
  { name: "Chanel", logo: null },
  { name: "Miu Miu", logo: null },
];

export default function CollectionsPage() {
  return (
    <div data-testid="collections-page" className="min-h-screen bg-[#F9F9F7]">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#1A1A1A] text-white">
        <div className="container-premium text-center">
          <p className="caption text-[#D4AF37] mb-4">Nos Collections</p>
          <h1 className="font-serif text-4xl md:text-6xl font-medium tracking-tight leading-[1.1] mb-6">
            Lunettes Solaires & Optiques
          </h1>
          <p className="text-lg font-light text-white/70 max-w-2xl mx-auto mb-8">
            Découvrez notre sélection de montures des plus grandes maisons. 
            Style, qualité et protection pour chaque regard.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Sun className="text-[#D4AF37]" size={24} />
            <span className="text-white/50 text-sm uppercase tracking-widest">Collection Été 2026</span>
            <Sun className="text-[#D4AF37]" size={24} />
          </div>
        </div>
      </section>

      {/* Video Placeholder - Collection Été 2026 */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="text-center mb-8">
            <p className="caption text-[#D4AF37] mb-2">Nouveautés</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4 text-[#1A1A1A]">
              Collection Été 2026
            </h2>
          </div>
          
          {/* Video Placeholder */}
          <div 
            className="relative aspect-video max-w-4xl mx-auto bg-[#1A1A1A] rounded-lg overflow-hidden flex items-center justify-center"
            data-testid="collection-video-placeholder"
          >
            <div className="text-center text-white/50">
              <Sun size={64} className="mx-auto mb-4 text-[#D4AF37]/50" />
              <p className="text-lg font-light">Vidéo à venir</p>
              <p className="text-sm text-white/30 mt-2">Collection Été 2026</p>
            </div>
            {/* When video is ready, replace with:
            <video 
              src="VIDEO_URL_HERE"
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
            */}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-white">
        <div className="container-premium">
          <div className="text-center mb-12">
            <p className="caption text-[#D4AF37] mb-2">Catégories</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4 text-[#1A1A1A]">
              Trouvez Votre Style
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Solaires */}
            <div className="group relative overflow-hidden bg-[#1A1A1A] rounded-lg aspect-[4/3] flex items-end p-8 cursor-pointer hover:shadow-xl transition-shadow">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1511499767150-a48a237f0083?w=800')] bg-cover bg-center opacity-60 group-hover:scale-105 transition-transform duration-500" />
              <div className="relative z-20">
                <Sun className="text-[#D4AF37] mb-3" size={32} />
                <h3 className="font-serif text-2xl text-white mb-2">Lunettes Solaires</h3>
                <p className="text-white/70 font-light text-sm mb-4">Protection UV et style pour l'été</p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-wider">
                  Découvrir <ArrowRight size={16} />
                </span>
              </div>
            </div>

            {/* Optiques */}
            <div className="group relative overflow-hidden bg-[#1A1A1A] rounded-lg aspect-[4/3] flex items-end p-8 cursor-pointer hover:shadow-xl transition-shadow">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=800')] bg-cover bg-center opacity-60 group-hover:scale-105 transition-transform duration-500" />
              <div className="relative z-20">
                <Sparkles className="text-[#D4AF37] mb-3" size={32} />
                <h3 className="font-serif text-2xl text-white mb-2">Lunettes Optiques</h3>
                <p className="text-white/70 font-light text-sm mb-4">Élégance et confort au quotidien</p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-wider">
                  Découvrir <ArrowRight size={16} />
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="text-center mb-12">
            <p className="caption text-[#D4AF37] mb-2">Nos Marques</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4 text-[#1A1A1A]">
              Les Plus Grandes Maisons
            </h2>
            <p className="text-[#666660] font-light max-w-xl mx-auto">
              Une sélection rigoureuse des meilleures marques de lunetterie pour vous offrir qualité et style.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {FEATURED_BRANDS.map((brand) => (
              <div 
                key={brand.name}
                className="bg-white p-6 rounded-lg flex items-center justify-center hover:shadow-md transition-shadow cursor-pointer group"
              >
                <span className="font-serif text-lg text-[#666660] group-hover:text-[#1A1A1A] transition-colors">
                  {brand.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="container-premium">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sun size={28} className="text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl mb-2">Verres Solaires</h3>
              <p className="text-[#666660] font-light text-sm">
                Protection UV 400 et verres polarisants disponibles sur tous nos modèles
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles size={28} className="text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl mb-2">Verres Correcteurs</h3>
              <p className="text-[#666660] font-light text-sm">
                Possibilité d'adapter vos solaires à votre vue avec des verres progressifs
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#D4AF37]">
                  <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2v5Z"/>
                  <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/>
                </svg>
              </div>
              <h3 className="font-serif text-xl mb-2">Conseil Expert</h3>
              <p className="text-[#666660] font-light text-sm">
                Notre équipe vous guide pour trouver la monture parfaite pour votre visage
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="container-premium text-center">
          <h2 className="font-serif text-3xl md:text-4xl mb-4 text-white">
            Envie d'essayer ?
          </h2>
          <p className="text-white/70 font-light mb-8 max-w-xl mx-auto">
            Venez découvrir nos collections en boutique. Notre équipe sera ravie de vous accueillir.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A1A1A] px-8 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#C4A030] transition-colors"
            >
              Prendre Rendez-vous
            </Link>
            <a
              href="https://wa.me/33781477380"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/30 px-8 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-white/10 transition-colors"
            >
              Nous Contacter
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
